<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import SingleSelectBox from '~/components/atoms/forms/SingleSelectBox.vue'
import { useAsyncDataWithCapture } from '~/composables/useAsyncDataWithCapture'
import { useCurrentLocation } from '~/composables/useCurrentLocation'
import { useCurrentUser } from '~/composables/useCurrentUser'
import { useToast } from '~/composables/useToast'
import { getLocations } from '~/repositories/locationRepository'

const route = useRoute()
const { currentUser } = useCurrentUser()
const { currentLocationId } = useCurrentLocation()
const { t } = useI18n()
const { addToast } = useToast()

const { data: locations } = await useAsyncDataWithCapture(
  async () => {
    if (!currentUser.value) return []
    const locationIds = currentUser.value.locations.map((location) => location.id)
    return await getLocations(locationIds)
  },
  {
    watch: [currentUser],
    transform: (locations) => {
      return locations.map((location) => ({
        label: location.name,
        value: location.id,
        searchKey: location.name.toLowerCase(),
      }))
    },
  }
)

type Option = {
  label: string
  value: string | number
}

const selectedLocation = computed<Option | undefined>(() => {
  if (currentLocationId.value === null) return undefined
  const location = locations.value?.find((location) => location.value === currentLocationId.value)
  if (location?.label) {
    return {
      label: location.label,
      value: currentLocationId.value,
    }
  }
  return undefined
})

const switchLocationOption = async (value: Option | undefined) => {
  if (!value) return
  const locationId = Number(value.value)
  if (currentLocationId.value === locationId) return
  currentLocationId.value = locationId
  await refreshNuxtData()
  await navigateTo({
    name: route.name?.toString(),
    params: { ...route.params, locationId },
  })
  addToast({
    color: 'success',
    message: t('拠点を切り替えました。'),
  })
}
</script>

<template>
  <SingleSelectBox
    v-if="locations && currentUser"
    :selected-option="selectedLocation"
    :options="locations"
    :placeholder="''"
    :width="'220px'"
    :options-max-height="380"
    :default-selected-option="selectedLocation"
    :clearable="false"
    @update:selected-option="switchLocationOption"
  />
</template>
